import Button from "@/components/Button";
import E164PhoneNumberInput from "@/components/E164PhoneNumberInput";
import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { PhoneNumber } from "@/types";
import { PATCH_FACILITY_MUTATION } from "@/features/facility/queries";
import { EditProps } from "./types";
import { NullableStringSchema } from "./validation";

const PhoneSchema = z.object({
  phone: NullableStringSchema,
  fax: NullableStringSchema,
  golfShopPhone: NullableStringSchema
});

type FieldValues = z.infer<typeof PhoneSchema>;

interface PhoneEditFormProps extends EditProps {
  phone?: PhoneNumber | null;
  fax?: PhoneNumber | null;
  golfShopPhone?: PhoneNumber | null;
}

function preparePhoneNumberForForm(phone: PhoneNumber | null | undefined): string {
  if (!phone) return "";
  return [phone.phoneNumber, phone.phoneExtension].filter(Boolean).join(";");
}

const defaultErrorMessage = "Failed to update phone numbers";

export default function PhoneEditForm({
  facilityId,
  phone: initialPhone,
  fax: initialFax,
  golfShopPhone: initialGolfShopPhone,
  onCancel,
  onSuccess,
  onError
}: PhoneEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      phone: preparePhoneNumberForForm(initialPhone),
      fax: preparePhoneNumberForForm(initialFax),
      golfShopPhone: preparePhoneNumberForForm(initialGolfShopPhone)
    },
    resolver: zodResolver(PhoneSchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit(values: FieldValues) {
    try {
      const input = {
        id: facilityId,
        ...values
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="phone-edit-form">
      <E164PhoneNumberInput
        value={form.watch("phone") || ""}
        label="Business Phone Number"
        onChange={(e) => form.setValue("phone", e)}
        onError={(e) => (e != null ? form.setError("phone", { message: e }) : form.clearErrors("phone"))}
        disabled={loading}
        error={!!form.formState.errors.phone}
        errorText={form.formState.errors.phone?.message}
      />
      <E164PhoneNumberInput
        value={form.watch("fax") || ""}
        label="Fax Number"
        onChange={(e) => form.setValue("fax", e)}
        onError={(e) => (e != null ? form.setError("fax", { message: e }) : form.clearErrors("fax"))}
        disabled={loading}
        error={!!form.formState.errors.fax}
        errorText={form.formState.errors.fax?.message}
      />
      <E164PhoneNumberInput
        value={form.watch("golfShopPhone") || ""}
        label="Golf Shop Phone Number"
        onChange={(e) => form.setValue("golfShopPhone", e)}
        onError={(e) =>
          e != null ? form.setError("golfShopPhone", { message: e }) : form.clearErrors("golfShopPhone")
        }
        disabled={loading}
        error={!!form.formState.errors.golfShopPhone}
        errorText={form.formState.errors.golfShopPhone?.message}
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
